"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const screenshots = [{
  title: "Dashboard Interface",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/dashboard_interface.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/dashboard_interface.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/dashboard_interface.webp-wvc-sizes",
  alt: "Dashboard interface showing key machine metrics and status"
}, {
  title: "Alarm Cockpit",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/alarm_cockpit.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/alarm_cockpit.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/alarm_cockpit.png-wvc-sizes",
  alt: "Alarm cockpit displaying real-time machine alerts and historical data"
}, {
  title: "Multi-Machine Overview",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/multi_machine_overview.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/multi_machine_overview.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/multi_machine_overview.png-wvc-sizes",
  alt: "Comprehensive overview page summarizing the entire machine fleet"
}, {
  title: "Settings & Configuration",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/settings_screens.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/settings_screens.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/settings_screens.png-wvc-sizes",
  alt: "Settings screens for customizing trigger rules and connectivity"
}];
export default function ProductGallery() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="product-gallery" className="bg-background py-16 md:py-24 overflow-hidden">
      <div ref={ref} className={cn("container mx-auto px-4 md:px-6 max-w-7xl transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground mb-4">
            Interface Overview
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">Explore the powerful features of Siemens Manage MyMachines through our intuitive dashboard, alarm cockpit, and comprehensive multi-machine views.</p>
        </div>

        <div className="relative px-8 md:px-12">
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {screenshots.map((shot, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
                  <div className="flex flex-col gap-4 group">
                    <div className="overflow-hidden rounded-xl shadow-md border border-border transition-shadow duration-300 group-hover:shadow-lg bg-card">
                      <AspectRatio ratio={16 / 9}>
                        <img src={shot.src} data-wvc-srcset={shot.srcset} data-wvc-sizes={shot.sizes} alt={shot.alt} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                      </AspectRatio>
                    </div>
                    <h3 className="font-default text-lg font-semibold text-foreground text-center">
                      {shot.title}
                    </h3>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-4 lg:-left-12 bg-background/80 backdrop-blur-sm hover:bg-background" />
            <CarouselNext className="hidden md:flex -right-4 lg:-right-12 bg-background/80 backdrop-blur-sm hover:bg-background" />
          </Carousel>
        </div>
      </div>
    </section>;
}